import PropTypes from 'prop-types';
import DismissablePopover from '@vh/components/DismissablePopover/DismissablePopover';
import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import * as Styled from './RecordingPopovers.styled';
import { useSelector } from 'react-redux';
import { useReactiveVar } from '@apollo/client';
import {
  setVhMicTooltipDismissedForever,
  vhMicTooltipDismissedForeverVar,
} from '@base/ApolloReactiveVars';
import { ChatStates, selectChatState } from '@vh/slices/chat/ChatSlice';
import { t } from 'i18next';

/**
 * @type {FC<{
 *  attachToRef: React.RefObject<HTMLButtonElement> | null;
 *  setShowMicUnavailable: (show: boolean) => void;
 *  showMicUnavailable: boolean;
 *  networkError: string | boolean | null
 * }>}
 */
const RecordingPopovers = ({
  attachToRef,
  setShowMicUnavailable,
  showMicUnavailable,
  networkError,
}) => {
  const vhMicTooltipDismissedForever = useReactiveVar(
    vhMicTooltipDismissedForeverVar
  );
  const [showMicHelpPopover, setShowMicHelpPopover] = useState(false);
  const chatState = useSelector(selectChatState);
  const setInactiveTooltip = useCallback(() => {
    setShowMicHelpPopover(false);
  }, []);

  const dismissForever = useCallback(() => {
    setShowMicHelpPopover(false);
    setVhMicTooltipDismissedForever(true);
  }, []);

  useEffect(() => {
    if (
      (chatState === ChatStates.TALK ||
        chatState === ChatStates.AWAITING_FIRST_CHAT) &&
      !vhMicTooltipDismissedForever
    ) {
      setShowMicHelpPopover(true);
    }
  }, [chatState, setShowMicHelpPopover, vhMicTooltipDismissedForever]);

  const setCloseMicPermissionsPopover = useCallback(() => {
    setShowMicUnavailable(false);
  }, [setShowMicUnavailable]);

  const [showNetworkError, setShowNetworkError] = useState(false);
  useEffect(() => {
    if (networkError) {
      setShowNetworkError(true);
    }
  }, [networkError]);

  const setCloseNetworkErrorPopover = useCallback(() => {
    setShowNetworkError(false);
  }, [setShowNetworkError]);

  return (
    <>
      {showMicUnavailable && (
        <DismissablePopover
          attachToRef={attachToRef}
          position={'top'}
          active={showMicUnavailable}
          setInactive={setCloseMicPermissionsPopover}
          type="error"
        >
          <Styled.DismissableContent>
            <div>{t('pleaseEnableMicrophonePermissionsInBrowser')}</div>
          </Styled.DismissableContent>
        </DismissablePopover>
      )}
      {showNetworkError && (
        <DismissablePopover
          attachToRef={attachToRef}
          position={'top'}
          active={showNetworkError}
          setInactive={setCloseNetworkErrorPopover}
          type="error"
        >
          <Styled.DismissableContent>
            <div>{t('errorWithSpeechServiceConnection')}</div>
          </Styled.DismissableContent>
        </DismissablePopover>
      )}
      {showMicHelpPopover && (
        <DismissablePopover
          attachToRef={attachToRef}
          position={'top'}
          active={showMicHelpPopover}
          setInactive={setInactiveTooltip}
          type="info"
        >
          <Styled.DismissableContent>
            <div>{t('clickHereToUseMicrophone')}</div>
            <Styled.DismissForever onClick={dismissForever}>
              {t('dontShowAgain')}
            </Styled.DismissForever>
          </Styled.DismissableContent>
        </DismissablePopover>
      )}
    </>
  );
};

RecordingPopovers.propTypes = {
  setShowMicUnavailable: PropTypes.func.isRequired,
  showMicUnavailable: PropTypes.bool.isRequired,
};

export default RecordingPopovers;
